import { computed, watch, Ref } from '@vue/composition-api';

interface UseMembershipPriceOptions {
  billingPlanKey: string | Ref<string | undefined>;
  defaultPrice?: number;
}

/**
 * Composable for fetching and returning dynamic membership prices based on currency
 * @param options - Configuration options
 * @param options.billingPlanKey - The billing plan key (e.g., 'fc_monthly_39') or a ref to it
 * @param options.defaultPrice - Default price in cents if no override is found (default: 3900)
 * @returns Computed membership price in cents
 */

const currencyMap: Record<string, string> = {
  CA: 'CAD',
  US: 'USD',
};

export function useMembershipPrice(options: UseMembershipPriceOptions) {
  const { billingPlanKey, defaultPrice = 3900 } = options;
  const { $store } = useNuxtApp();

  const membershipPrice = computed(() => {
    const country = $store.state.checkout?.shipping?.country || $store.state.app.geoLocationCountry;

    const currency = currencyMap[country] || 'USD';

    const key = typeof billingPlanKey === 'string' ? billingPlanKey : billingPlanKey.value;

    if (!key) return defaultPrice;

    const overridePrice = $store.getters.subscriptionAmount(key, currency);
    return overridePrice ?? defaultPrice;
  });

  // Ensure subscription display prices are loaded
  watch(
    () => ({
      billingPlanKey: typeof billingPlanKey === 'string' ? billingPlanKey : billingPlanKey.value,
      country: $store.state.checkout?.shipping?.country || $store.state.app.geoLocationCountry,
    }),
    ({ billingPlanKey: key, country }) => {
      if (key && country) {
        const currency = currencyMap[country] || 'USD';

        $store.dispatch('ensureSubscriptionDisplayPrices', {
          billingPlanKeys: [key],
          currency,
        });
      }
    },
    { immediate: true },
  );

  return {
    membershipPrice,
  };
}
